import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useInventoryInsights } from '@/hooks/useInventoryInsights'
import { Package, AlertTriangle, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ScrollArea } from '@/components/ui/scroll-area'
import { IngredientIcon } from '@/components/ui/ingredient-icon'

interface InventoryInsightsProps {
  restaurantId: string | null
}

export function InventoryInsights({ restaurantId }: InventoryInsightsProps) {
  const { t, i18n } = useTranslation()
  const { data: insights, isLoading } = useInventoryInsights(restaurantId)

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardHeader className="pb-2">
              <div className="h-4 bg-muted animate-pulse rounded w-24" />
            </CardHeader>
            <CardContent>
              <div className="h-8 bg-muted animate-pulse rounded w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  if (!insights) {
    return null
  }

  return (
    <div className="grid gap-4 md:grid-cols-3">
      {/* Total Ingredients Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            {t('inventory.insights.totalIngredients')}
          </CardTitle>
          <Package className="h-4 w-4 text-blue-600" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{insights.totalIngredients}</div>
          <p className="text-xs text-muted-foreground mt-1">
            {t('inventory.insights.activeIngredients')}
          </p>
        </CardContent>
      </Card>

      {/* Low Stock Card with ingredient names */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            {t('inventory.insights.lowStock')}
          </CardTitle>
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{insights.lowStockItems.length}</div>
          {insights.lowStockItems.length > 0 ? (
            <ScrollArea className="h-24 mt-2">
              <div className="space-y-1">
                {insights.lowStockItems.map((item) => {
                  const ingredientName = i18n.language === 'ar' 
                    ? (item.nameAr || item.nameFr) 
                    : (item.nameFr || item.nameAr);
                  return (
                    <div key={item.ingredientId} className="text-xs text-muted-foreground flex items-center gap-1.5">
                      <IngredientIcon
                        ingredient={{
                          nameAr: item.nameAr,
                          nameFr: item.nameFr,
                          emoji: item.emoji
                        }}
                        size={16}
                        className="flex-shrink-0"
                      />
                      {ingredientName && (
                        <span className="flex-1 truncate">{ingredientName}</span>
                      )}
                      <span className="text-yellow-600 flex-shrink-0 whitespace-nowrap">({item.quantity} {item.unit})</span>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          ) : (
            <p className="text-xs text-muted-foreground mt-1">
              {t('inventory.insights.allStocked')}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Out of Stock Card with ingredient names */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            {t('inventory.insights.outOfStock')}
          </CardTitle>
          <XCircle className="h-4 w-4 text-red-600" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{insights.outOfStockItems.length}</div>
          {insights.outOfStockItems.length > 0 ? (
            <ScrollArea className="h-24 mt-2">
              <div className="space-y-1">
                {insights.outOfStockItems.map((item) => {
                  const ingredientName = i18n.language === 'ar' 
                    ? (item.nameAr || item.nameFr) 
                    : (item.nameFr || item.nameAr);
                  return (
                    <div key={item.ingredientId} className="text-xs text-muted-foreground flex items-center gap-1.5">
                      <IngredientIcon
                        ingredient={{
                          nameAr: item.nameAr,
                          nameFr: item.nameFr,
                          emoji: item.emoji
                        }}
                        size={16}
                        className="flex-shrink-0"
                      />
                      {ingredientName && (
                        <span className="flex-1 truncate">{ingredientName}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          ) : (
            <p className="text-xs text-muted-foreground mt-1">
              {t('inventory.insights.allStocked')}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

